import type { QuickReplyAsk } from "@wags/concierge-agent-programs/kits/ui-tools.ts";
import type { AssistAnswerPatch } from "@wags/viewer/profile/onboarding.contract.ts";

export type AssistFact = keyof AssistAnswerPatch;

export type QuickReplyOption = { label: string; value: string };

export type QuickReplySet = {
    prompt: string;
    options: QuickReplyOption[];

    fact?: AssistFact;
};

export const QUICK_REPLY_SETS = {
    timeline: {
        fact: "timeline",
        prompt: "When are you hoping to bring one home?",
        options: [
            { label: "Ready now", value: "ready_now" },
            { label: "In the next few months", value: "two_to_three_months" },
            { label: "Just browsing", value: "just_browsing" },
        ],
    },
    pets: {
        fact: "pets",
        prompt: "Any other pets at home?",
        options: [
            { label: "A cat", value: "cat" },
            { label: "A dog", value: "dog" },
            { label: "Both", value: "both" },
            { label: "None", value: "none" },
        ],
    },
    intent: {
        fact: "intent",
        prompt: "Adopting, or finding a breeder?",
        options: [
            { label: "Adopting from a shelter", value: "shelter" },
            { label: "Finding a breeder", value: "breeder" },
            { label: "Not sure yet", value: "browsing" },
        ],
    },
    transport: {
        fact: "transport",
        prompt: "Open to out-of-state transport?",
        options: [
            { label: "Yes, I'm open", value: "yes" },
            { label: "Local only", value: "no" },
            { label: "Not sure", value: "not_sure" },
        ],
    },
    housing: {
        fact: "housing",
        prompt: "What's your home like?",
        options: [
            { label: "Apartment", value: "apartment" },
            { label: "House with a yard", value: "house_yard" },
            { label: "Renting, no yard", value: "renting_no_yard" },
        ],
    },
    kids: {
        fact: "kids",
        prompt: "Young kids at home?",
        options: [
            { label: "Yes", value: "yes" },
            { label: "No", value: "no" },
        ],
    },

    readiness: {
        prompt: "Ready to reach out? No pressure either way.",
        options: [
            { label: "Yes, let's talk", value: "yes" },
            { label: "Still deciding", value: "no" },
        ],
    },
} satisfies Record<QuickReplyAsk, QuickReplySet>;

export type QuickReplyKey = keyof typeof QUICK_REPLY_SETS;

export const QUICK_REPLY_KEYS = Object.keys(QUICK_REPLY_SETS) as [
    QuickReplyKey,
    ...QuickReplyKey[],
];

export function setFor(ask: QuickReplyKey): QuickReplySet {
    return QUICK_REPLY_SETS[ask];
}

export function optionFor(
    ask: QuickReplyKey,
    value: string,
): QuickReplyOption | undefined {
    return setFor(ask).options.find((option) => option.value === value);
}

export function isQuickReplyKey(value: string): value is QuickReplyKey {
    return value in QUICK_REPLY_SETS;
}

export function valueForLabel(
    ask: QuickReplyKey,
    label: string,
): string | undefined {
    if (label === DECLINE_LABEL)
        return isDeclinable(ask) ? DECLINE_VALUE : undefined;
    return setFor(ask).options.find((option) => option.label === label)?.value;
}

export const DECLINE_VALUE = "__decline";
export const DECLINE_LABEL = "Rather not say";

export function isDeclinable(ask: QuickReplyKey): boolean {
    return setFor(ask).fact !== undefined;
}
